import { useEffect, useState, useCallback } from "react";
import { useAdminApi } from "../hooks/useAdminApi";
import { TextField, Select, SelectItem, Checkbox, Button } from "@firecms/ui";

interface BadgeDef {
    id: string;
    name: string;
    description: string;
    category: string;
    tier: string;
    criteria: Record<string, any>;
    icon_url?: string;
    is_nft_eligible: boolean;
    is_soulbound: boolean;
    created_at?: string;
    updated_at?: string;
}

const CATEGORIES = [
    { id: "streak", label: "🔥 Streak" },
    { id: "session", label: "📚 Session" },
    { id: "skill", label: "🎯 Skill" },
    { id: "cefr", label: "🎓 CEFR" },
    { id: "social", label: "🤝 Social" },
    { id: "special", label: "⭐ Special" },
];

const TIERS = [
    { id: "bronze", label: "🥉 Bronze" },
    { id: "silver", label: "🥈 Silver" },
    { id: "gold", label: "🥇 Gold" },
    { id: "platinum", label: "💎 Platinum" },
    { id: "legendary", label: "🏆 Legendary" },
];

const EMPTY_FORM: Omit<BadgeDef, "created_at" | "updated_at"> = {
    id: "",
    name: "",
    description: "",
    category: "streak",
    tier: "bronze",
    criteria: {},
    icon_url: "",
    is_nft_eligible: false,
    is_soulbound: false,
};

const CRITERIA_KEYS = [
    "streak", "total_sessions", "cefr_level", "referrals", "shares",
    "first_roleplay", "first_study", "first_assessment", "first_voice", "early_adopter",
];

export function BadgeDefinitionsView() {
    const [badges, setBadges] = useState<BadgeDef[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState<string | null>(null);
    const [seeding, setSeeding] = useState(false);
    const [notification, setNotification] = useState<{ type: "success" | "error"; message: string } | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [criteriaKey, setCriteriaKey] = useState("");
    const [criteriaValue, setCriteriaValue] = useState("");
    const [filterCategory, setFilterCategory] = useState<string>("");
    const { fetchApi } = useAdminApi();

    const loadBadges = useCallback(async () => {
        setLoading(true);
        try {
            const url = filterCategory
                ? `/admin/api/badge-definitions?category=${filterCategory}`
                : "/admin/api/badge-definitions";
            const data = await fetchApi(url);
            setBadges(data);
        } catch (error: any) {
            setNotification({ type: "error", message: error.message || "Failed to load badges" });
        } finally {
            setLoading(false);
        }
    }, [fetchApi, filterCategory]);

    useEffect(() => { loadBadges(); }, [loadBadges]);

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setEditingId(null);
        setShowForm(true);
    };

    const openEdit = (badge: BadgeDef) => {
        setForm({
            id: badge.id,
            name: badge.name,
            description: badge.description,
            category: badge.category,
            tier: badge.tier,
            criteria: badge.criteria || {},
            icon_url: badge.icon_url || "",
            is_nft_eligible: badge.is_nft_eligible,
            is_soulbound: badge.is_soulbound,
        });
        setEditingId(badge.id);
        setShowForm(true);
    };

    const cancelForm = () => {
        setShowForm(false);
        setEditingId(null);
        setForm(EMPTY_FORM);
    };

    const handleSave = async () => {
        setSaving(true);
        setNotification(null);
        try {
            if (editingId) {
                const { id, ...updates } = form;
                await fetchApi(`/admin/api/badge-definitions/${editingId}`, {
                    method: "PATCH",
                    body: JSON.stringify(updates),
                });
                setNotification({ type: "success", message: `Badge "${form.name}" updated` });
            } else {
                await fetchApi("/admin/api/badge-definitions", {
                    method: "POST",
                    body: JSON.stringify(form),
                });
                setNotification({ type: "success", message: `Badge "${form.name}" created` });
            }
            cancelForm();
            await loadBadges();
        } catch (error: any) {
            setNotification({ type: "error", message: error.message || "Save failed" });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (badge: BadgeDef) => {
        if (!window.confirm(`Delete badge "${badge.name}" (${badge.id})? This won't remove already-earned badges.`)) return;
        setDeleting(badge.id);
        setNotification(null);
        try {
            await fetchApi(`/admin/api/badge-definitions/${badge.id}`, { method: "DELETE" });
            setNotification({ type: "success", message: `Badge "${badge.name}" deleted` });
            await loadBadges();
        } catch (error: any) {
            setNotification({ type: "error", message: error.message || "Delete failed" });
        } finally {
            setDeleting(null);
        }
    };

    const handleSeedDefaults = async () => {
        setSeeding(true);
        setNotification(null);
        try {
            const result = await fetchApi("/admin/api/badge-definitions/seed", { method: "POST" });
            setNotification({ type: "success", message: `Seed complete: ${result.created} created, ${result.skipped} already existed` });
            await loadBadges();
        } catch (error: any) {
            setNotification({ type: "error", message: error.message || "Seed failed" });
        } finally {
            setSeeding(false);
        }
    };

    const addCriteria = () => {
        if (!criteriaKey) return;
        const parsed = Number(criteriaValue);
        const val = criteriaValue === "true" ? true : criteriaValue === "false" ? false : isNaN(parsed) ? criteriaValue : parsed;
        setForm(f => ({ ...f, criteria: { ...f.criteria, [criteriaKey]: val } }));
        setCriteriaKey("");
        setCriteriaValue("");
    };

    const removeCriteria = (key: string) => {
        setForm(f => {
            const next = { ...f.criteria };
            delete next[key];
            return { ...f, criteria: next };
        });
    };

    const getCategoryLabel = (id: string) => CATEGORIES.find(c => c.id === id)?.label || id;
    const getTierLabel = (id: string) => TIERS.find(t => t.id === id)?.label || id;

    return (
        <div className="p-8 max-w-6xl mx-auto space-y-8 min-h-screen">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div>
                    <h1 className="text-4xl font-black text-white tracking-tight uppercase">Badge Definitions</h1>
                    <p className="text-slate-500 mt-2 text-lg">Create, edit, and delete badge types. Changes take effect within 5 minutes.</p>
                </div>
                <div className="flex gap-3 self-start md:self-auto flex-wrap">
                    {badges.length === 0 && !loading && (
                        <button
                            onClick={handleSeedDefaults}
                            disabled={seeding}
                            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-700 disabled:text-slate-500 text-white font-bold rounded-2xl transition-all flex items-center gap-2"
                        >
                            {seeding ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Seeding…
                                </>
                            ) : "🌱 Seed Defaults"}
                        </button>
                    )}
                    <button
                        onClick={openCreate}
                        disabled={showForm}
                        className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-700 disabled:text-slate-500 text-white font-bold rounded-2xl transition-all flex items-center gap-2"
                    >
                        + Create Badge
                    </button>
                </div>
            </div>

            {/* Notification */}
            {notification && (
                <div className={`p-4 rounded-2xl border ${notification.type === "success" ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400" : "bg-red-500/10 border-red-500/20 text-red-400"}`}>
                    {notification.message}
                    <button onClick={() => setNotification(null)} className="ml-4 opacity-50 hover:opacity-100">✕</button>
                </div>
            )}

            {/* Create / Edit Form */}
            {showForm && (
                <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 space-y-6">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <span className="w-1.5 h-6 bg-emerald-500 rounded-full" />
                        {editingId ? `Edit: ${editingId}` : "New Badge Definition"}
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* ID (only on create) */}
                        {!editingId && (
                            <div>
                                <label className="block text-slate-400 text-xs font-bold uppercase tracking-widest mb-2">ID (snake_case)</label>
                                <TextField
                                    value={form.id}
                                    onChange={(e) => setForm(f => ({ ...f, id: e.target.value }))}
                                    placeholder="e.g. week_warrior"
                                    size="small"
                                />
                            </div>
                        )}
                        <div>
                            <label className="block text-slate-400 text-xs font-bold uppercase tracking-widest mb-2">Name</label>
                            <TextField
                                value={form.name}
                                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                                placeholder="Week Warrior"
                                size="small"
                            />
                        </div>
                        <div className={editingId ? "" : "md:col-span-2"}>
                            <label className="block text-slate-400 text-xs font-bold uppercase tracking-widest mb-2">Description</label>
                            <TextField
                                value={form.description}
                                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                                placeholder="Maintain a 7-day streak"
                                size="small"
                            />
                        </div>
                        <div>
                            <label className="block text-slate-400 text-xs font-bold uppercase tracking-widest mb-2">Category</label>
                            <Select
                                value={form.category}
                                onValueChange={(v) => setForm(f => ({ ...f, category: v }))}
                                renderValue={(v) => getCategoryLabel(v)}
                                size="small"
                            >
                                {CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                            </Select>
                        </div>
                        <div>
                            <label className="block text-slate-400 text-xs font-bold uppercase tracking-widest mb-2">Tier</label>
                            <Select
                                value={form.tier}
                                onValueChange={(v) => setForm(f => ({ ...f, tier: v }))}
                                renderValue={(v) => getTierLabel(v)}
                                size="small"
                            >
                                {TIERS.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                            </Select>
                        </div>
                        <div>
                            <label className="block text-slate-400 text-xs font-bold uppercase tracking-widest mb-2">Icon URL</label>
                            <TextField
                                value={form.icon_url || ""}
                                onChange={(e) => setForm(f => ({ ...f, icon_url: e.target.value }))}
                                placeholder="https://storage.googleapis.com/..."
                                size="small"
                            />
                        </div>
                        <div className="flex items-center gap-6 pt-4">
                            <Checkbox
                                checked={form.is_nft_eligible}
                                onCheckedChange={(v) => setForm(f => ({ ...f, is_nft_eligible: !!v }))}
                            />
                            <span className="text-white text-sm">NFT Eligible</span>
                            <Checkbox
                                checked={form.is_soulbound}
                                onCheckedChange={(v) => setForm(f => ({ ...f, is_soulbound: !!v }))}
                            />
                            <span className="text-white text-sm">Soulbound (SBT)</span>
                        </div>
                    </div>

                    {/* Criteria builder */}
                    <div className="space-y-3">
                        <label className="block text-slate-400 text-xs font-bold uppercase tracking-widest">Criteria</label>
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(form.criteria).map(([k, v]) => (
                                <span key={k} className="px-3 py-1 bg-slate-800 text-slate-300 text-sm rounded-lg flex items-center gap-2 border border-slate-700">
                                    <span className="font-mono">{k}: {String(v)}</span>
                                    <button onClick={() => removeCriteria(k)} className="text-slate-500 hover:text-red-400">✕</button>
                                </span>
                            ))}
                        </div>
                        <div className="flex gap-3 items-end">
                            <div className="flex-1">
                                <Select
                                    value={criteriaKey}
                                    onValueChange={setCriteriaKey}
                                    renderValue={(v) => v || "Select key"}
                                    size="small"
                                    label="Key"
                                >
                                    {CRITERIA_KEYS.filter(k => !(k in form.criteria)).map(k => (
                                        <SelectItem key={k} value={k}>{k}</SelectItem>
                                    ))}
                                </Select>
                            </div>
                            <div className="flex-1">
                                <TextField
                                    value={criteriaValue}
                                    onChange={(e) => setCriteriaValue(e.target.value)}
                                    placeholder="Value (number, true/false)"
                                    size="small"
                                    label="Value"
                                />
                            </div>
                            <Button onClick={addCriteria} variant="outlined" size="small" disabled={!criteriaKey}>
                                Add
                            </Button>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-4 border-t border-slate-800">
                        <button
                            onClick={handleSave}
                            disabled={saving || (!editingId && !form.id) || !form.name || !form.description}
                            className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-700 disabled:text-slate-500 text-white font-bold rounded-2xl transition-all"
                        >
                            {saving ? "Saving…" : editingId ? "Update Badge" : "Create Badge"}
                        </button>
                        <button
                            onClick={cancelForm}
                            className="px-8 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold rounded-2xl transition-all"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Filter */}
            <div className="flex items-center gap-4">
                <span className="text-slate-500 text-sm font-bold uppercase tracking-widest">Filter:</span>
                <div className="flex gap-2 flex-wrap">
                    <button
                        onClick={() => setFilterCategory("")}
                        className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${!filterCategory ? "bg-white text-black" : "bg-slate-800 text-slate-400 hover:text-white"}`}
                    >
                        All ({badges.length})
                    </button>
                    {CATEGORIES.map(c => (
                        <button
                            key={c.id}
                            onClick={() => setFilterCategory(c.id)}
                            className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${filterCategory === c.id ? "bg-white text-black" : "bg-slate-800 text-slate-400 hover:text-white"}`}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Badges Table */}
            <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-slate-950/50">
                            <th className="p-4 text-slate-500 text-xs font-bold uppercase tracking-widest border-b border-slate-800">Badge</th>
                            <th className="p-4 text-slate-500 text-xs font-bold uppercase tracking-widest border-b border-slate-800">Category</th>
                            <th className="p-4 text-slate-500 text-xs font-bold uppercase tracking-widest border-b border-slate-800">Tier</th>
                            <th className="p-4 text-slate-500 text-xs font-bold uppercase tracking-widest border-b border-slate-800">NFT</th>
                            <th className="p-4 text-slate-500 text-xs font-bold uppercase tracking-widest border-b border-slate-800">Criteria</th>
                            <th className="p-4 text-slate-500 text-xs font-bold uppercase tracking-widest border-b border-slate-800">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            Array(5).fill(0).map((_, i) => (
                                <tr key={i} className="animate-pulse">
                                    <td className="p-4 border-b border-slate-800/50"><div className="h-4 bg-slate-800 rounded w-32" /></td>
                                    <td className="p-4 border-b border-slate-800/50"><div className="h-4 bg-slate-800 rounded w-16" /></td>
                                    <td className="p-4 border-b border-slate-800/50"><div className="h-4 bg-slate-800 rounded w-16" /></td>
                                    <td className="p-4 border-b border-slate-800/50"><div className="h-4 bg-slate-800 rounded w-10" /></td>
                                    <td className="p-4 border-b border-slate-800/50"><div className="h-4 bg-slate-800 rounded w-24" /></td>
                                    <td className="p-4 border-b border-slate-800/50"><div className="h-4 bg-slate-800 rounded w-20" /></td>
                                </tr>
                            ))
                        ) : badges.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="p-10 text-center text-slate-500">
                                    No badge definitions found. Seed defaults or create your first badge.
                                </td>
                            </tr>
                        ) : (
                            badges.map((badge) => (
                                <tr key={badge.id} className="hover:bg-slate-800/30 transition-colors">
                                    <td className="p-4 border-b border-slate-800/50">
                                        <div className="flex items-center gap-3">
                                            {badge.icon_url && (
                                                <img src={badge.icon_url} alt="" className="w-8 h-8 rounded-lg object-cover" />
                                            )}
                                            <div>
                                                <p className="text-white font-medium text-sm">{badge.name}</p>
                                                <p className="text-slate-600 text-[10px] font-mono">{badge.id}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-4 border-b border-slate-800/50 text-sm">{getCategoryLabel(badge.category)}</td>
                                    <td className="p-4 border-b border-slate-800/50 text-sm">{getTierLabel(badge.tier)}</td>
                                    <td className="p-4 border-b border-slate-800/50 text-sm">
                                        {badge.is_nft_eligible ? (
                                            <span className="text-emerald-400">{badge.is_soulbound ? "SBT" : "Yes"}</span>
                                        ) : (
                                            <span className="text-slate-600">No</span>
                                        )}
                                    </td>
                                    <td className="p-4 border-b border-slate-800/50">
                                        <div className="flex flex-wrap gap-1">
                                            {Object.entries(badge.criteria || {}).map(([k, v]) => (
                                                <span key={k} className="px-2 py-0.5 bg-slate-800 text-slate-400 text-[10px] rounded font-mono">
                                                    {k}: {String(v)}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="p-4 border-b border-slate-800/50">
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => openEdit(badge)}
                                                disabled={showForm}
                                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white text-xs font-bold rounded-lg transition-all"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(badge)}
                                                disabled={deleting === badge.id}
                                                className="px-3 py-1.5 bg-red-600/20 hover:bg-red-600/40 disabled:bg-slate-700 text-red-400 disabled:text-slate-500 text-xs font-bold rounded-lg transition-all border border-red-600/20"
                                            >
                                                {deleting === badge.id ? "…" : "Delete"}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
